import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuClock, LuInbox, LuListTodo, LuLoader, LuTriangleAlert } from 'react-icons/lu';
import Modal from '../../components/ui/Modal';
import GlassIcon from '../../components/ui/GlassIcon';
import SegmentedControl from '../../components/ui/SegmentedControl';
import { useTranslation } from '../../i18n/LanguageContext';
import useMyTasks from './useMyTasks';
import TaskCard from './components/TaskCard';

const STAT_TILES = [
  { key: 'pending', icon: LuClock, tint: 'tw-text-slate-500 dark:tw-text-slate-300' },
  { key: 'inProgress', icon: LuLoader, tint: 'tw-text-blue-600 dark:tw-text-blue-300' },
  { key: 'completed', icon: LuCheck, tint: 'tw-text-emerald-600 dark:tw-text-emerald-300' },
  { key: 'overdue', icon: LuTriangleAlert, tint: 'tw-text-rose-600 dark:tw-text-rose-300' },
];

/** "Görevlerim": the signed-in employee's tasks, opened from the top bar and the dashboard. */
const MyTasksModal = ({ isOpen, onClose }) => {
  const { t, locale, language } = useTranslation();
  const { myTasks, stats, isLoading, statusFilter, setStatusFilter, unseenTaskIds, updateStatus } = useMyTasks(isOpen);
  const [expandedTask, setExpandedTask] = useState(null);

  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  }, [locale]);

  const filterOptions = ['active', 'completed', 'all'].map((value) => ({ value, label: t(`tasks.filters.${value}`) }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      lang={language}
      title={t('tasks.title')}
      subtitle={t('tasks.subtitle', { count: stats.pending + stats.inProgress })}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-500 tw-shadow-teal-500/30">
          <LuListTodo aria-hidden="true" className="tw-h-6 tw-w-6" />
        </GlassIcon>
      )}
      closeLabel={t('common.close')}
      bodyClassName="tw-p-0"
    >
      <div className="tw-sticky tw-top-0 tw-z-10 tw-space-y-3 tw-border-b tw-border-slate-200/80 tw-bg-white/95 tw-px-4 tw-py-4 sm:tw-px-6 dark:tw-border-white/10 dark:tw-bg-[#1f232b]/95">
        <div className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
          {STAT_TILES.map(({ key, icon: Icon, tint }) => {
            const highlighted = key === 'overdue' && stats.overdue > 0;
            return (
              <div
                key={key}
                className={`tw-flex tw-items-center tw-gap-2.5 tw-rounded-2xl tw-border tw-px-3 tw-py-2.5 ${
                  highlighted
                    ? 'tw-border-rose-200 tw-bg-rose-50 dark:tw-border-rose-400/25 dark:tw-bg-rose-500/10'
                    : 'tw-border-slate-200/80 tw-bg-slate-50 dark:tw-border-white/10 dark:tw-bg-white/[0.04]'
                }`}
              >
                <Icon aria-hidden="true" className={`tw-h-4 tw-w-4 tw-shrink-0 ${tint}`} />
                <span className="tw-text-lg tw-font-bold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{stats[key]}</span>
                <span className="tw-min-w-0 tw-truncate tw-text-xs tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">
                  {t(`tasks.stats.${key}`)}
                </span>
              </div>
            );
          })}
        </div>
        <SegmentedControl
          options={filterOptions}
          value={statusFilter}
          onChange={setStatusFilter}
          layoutId="tasks-filter-pill"
          label={t('tasks.title')}
        />
      </div>

      <div className="tw-px-4 tw-py-4 sm:tw-px-6 sm:tw-py-5">
        {isLoading ? (
          <ul aria-label={t('tasks.loading')} className="tw-space-y-3">
            {[0, 1, 2].map((item) => (
              <li key={item} className="tw-h-[4.5rem] tw-animate-pulse tw-rounded-2xl tw-bg-slate-100 dark:tw-bg-white/[0.05]" />
            ))}
          </ul>
        ) : myTasks.length === 0 ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-12 tw-text-center">
            <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500">
              <LuInbox aria-hidden="true" className="tw-h-7 tw-w-7" />
            </span>
            <p className="tw-text-sm tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">
              {statusFilter === 'active' ? t('tasks.emptyActive') : t('tasks.empty')}
            </p>
          </div>
        ) : (
          <ul className="tw-space-y-3">
            {myTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                isNew={unseenTaskIds.has(task.id)}
                expanded={expandedTask === task.id}
                onToggle={() => setExpandedTask(expandedTask === task.id ? null : task.id)}
                onStatusChange={updateStatus}
                formatDate={formatDate}
              />
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
};

MyTasksModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MyTasksModal;
